/**
 * RogueDirectiveCard — Phase 20.5
 * --------------------------------------------------------------
 * Renders an active Rogue Directive on the user's dashboard. Three states:
 *   • current user IS the Laggard  → red alert banner with task + countdown
 *   • current user IS the Leader   → yellow verification panel with task +
 *                                    [ VERIFY ] / [ REJECT ] buttons
 *   • current user is neither      → null (they only got the push)
 *
 * Polls `/api/leagues/{league_id}/rogue/active` every 30s so a fresh drop
 * surfaces without a manual refresh.
 *
 * Includes a discreet [ DEV ] admin override button for admins to fire a
 * directive on demand during QA — gated by `user.account_type === 'admin'`
 * AND `process.env.NODE_ENV !== 'production'` so it never reaches end users.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

function useCountdown(expiresAtIso) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!expiresAtIso) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [expiresAtIso]);
  if (!expiresAtIso) return null;
  const remaining = Math.max(0, new Date(expiresAtIso).getTime() - now);
  const h = Math.floor(remaining / 3_600_000);
  const m = Math.floor((remaining % 3_600_000) / 60_000);
  const s = Math.floor((remaining % 60_000) / 1000);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export default function RogueDirectiveCard({ user, leagueId }) {
  const [directive, setDirective] = useState(null);
  const [busy, setBusy] = useState(false);
  const [triggering, setTriggering] = useState(false);

  const reload = useCallback(async () => {
    if (!leagueId) return;
    try {
      const r = await api.getActiveRogue(leagueId);
      // Only mutate state on a SUCCESSFUL response. The backend returns
      // {directive: null} explicitly when nothing is active. We must NOT
      // clear the card on a transient network/JWT/5xx error — that was
      // the root cause of the "vanishing at midnight" bug: any single
      // failed poll would purge the directive until the next 30s tick
      // (or the next tick also failed → card stayed gone).
      setDirective(r?.directive || null);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[RogueDirectiveCard] poll failed, retaining prior state:", e?.response?.status, e?.message);
      // Intentionally NO state mutation — the previous directive stays on
      // screen and the next poll (30s) will try again.
    }
  }, [leagueId]);

  useEffect(() => {
    reload();
    const t = setInterval(reload, 30_000);
    return () => clearInterval(t);
  }, [reload]);

  const countdown = useCountdown(directive?.expires_at);

  // Admin QA override (dev only, admin-account only)
  const onTrigger = async () => {
    setTriggering(true);
    try {
      await api.adminTriggerRogue(leagueId);
      toast.success("Rogue directive deployed");
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Trigger failed");
    } finally {
      setTriggering(false);
    }
  };

  const onVerify = async () => {
    // ---------- Phase 20.6 — Optimistic dismiss ----------
    const prev = directive;
    setDirective(null);                   // instantly remove the card
    setBusy(true);
    try {
      await api.verifyRogue(prev.id);
      toast.success(`+20 PTS awarded to ${prev.laggard_name}`);
    } catch (e) {
      setDirective(prev);                 // rollback
      const detail = e?.response?.data?.detail;
      toast.error(detail ? `[ REVERTED: ${String(detail).toUpperCase()} ]` : "[ REVERTED: NETWORK ERROR ]");
    } finally { setBusy(false); }
  };

  const onReject = async () => {
    const prev = directive;
    setDirective(null);                   // instantly remove the card
    setBusy(true);
    try {
      await api.rejectRogue(prev.id);
      toast(`Rejected — no payout`);
    } catch (e) {
      setDirective(prev);                 // rollback
      const detail = e?.response?.data?.detail;
      toast.error(detail ? `[ REVERTED: ${String(detail).toUpperCase()} ]` : "[ REVERTED: NETWORK ERROR ]");
    } finally { setBusy(false); }
  };

  // Dev override visible for admins even when no directive is active
  const showDevButton = user?.account_type === "admin" && process.env.NODE_ENV !== "production";

  if (!directive) {
    if (!showDevButton) return null;
    return (
      <div className="border border-[var(--stakes-border)] bg-black/30 px-4 py-3 flex items-center justify-between" data-testid="rogue-dev-bar">
        <div className="label-mono text-[var(--stakes-muted)]">ROGUE DIRECTIVE · DORMANT</div>
        <button
          onClick={onTrigger}
          disabled={triggering}
          className="label-mono px-3 py-1 border border-[var(--stakes-border)] hover:border-[var(--neon-blue)] hover:text-[var(--neon-blue)] disabled:opacity-40"
          data-testid="rogue-trigger-btn"
        >
          {triggering ? "[ DEPLOYING… ]" : "[ DEV · FIRE DROP ]"}
        </button>
      </div>
    );
  }

  const isLaggard = user?.id === directive.laggard_id;
  const isLeader = user?.id === directive.leader_id;

  // Only laggard + leader see the card; bystanders just got the push.
  if (!isLaggard && !isLeader) return null;

  const accent = isLaggard ? "var(--stakes-primary)" : "var(--stakes-child)";

  return (
    <div
      className="border-2 p-4 md:p-5"
      style={{ borderColor: accent, background: "rgba(0,0,0,0.55)", boxShadow: `0 0 28px ${accent}33` }}
      data-testid="rogue-directive-card"
      data-role={isLaggard ? "laggard" : "leader"}
    >
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="label-mono" style={{ color: accent }}>
          {isLaggard ? "[ ROGUE DIRECTIVE ACTIVATED ]" : "[ DIRECTIVE PENDING ]"}
        </div>
        {countdown && (
          <div className="label-mono tabular-nums" data-testid="rogue-countdown">
            EXPIRES IN <span style={{ color: accent }}>{countdown}</span>
          </div>
        )}
      </div>

      <h3 className="font-anton text-xl md:text-2xl uppercase mt-2 leading-snug" data-testid="rogue-task-text">
        {directive.task_text}
      </h3>

      <div className="label-mono mt-3 text-[var(--stakes-muted)]">
        {isLaggard ? (
          <>TARGET: <span className="text-white font-bold" data-testid="rogue-target-name">{directive.leader_name}</span></>
        ) : (
          <>OPERATIVE: <span className="text-white font-bold" data-testid="rogue-operative-name">{directive.laggard_name}</span></>
        )}
      </div>

      {isLeader && (
        <div className="mt-4 grid grid-cols-2 gap-3">
          <button
            onClick={onVerify}
            disabled={busy}
            className="border-2 border-[var(--neon-blue)] text-[var(--neon-blue)] hover:bg-[var(--neon-blue)] hover:text-black font-anton uppercase tracking-wider py-3 disabled:opacity-40 transition-colors"
            data-testid="rogue-verify-btn"
          >
            {busy ? "…" : "[ VERIFY ]"}
          </button>
          <button
            onClick={onReject}
            disabled={busy}
            className="border-2 border-[var(--stakes-primary)] text-[var(--stakes-primary)] hover:bg-[var(--stakes-primary)] hover:text-black font-anton uppercase tracking-wider py-3 disabled:opacity-40 transition-colors"
            data-testid="rogue-reject-btn"
          >
            {busy ? "…" : "[ REJECT ]"}
          </button>
        </div>
      )}

      {showDevButton && (
        <div className="mt-3 flex justify-end">
          <button
            onClick={onTrigger}
            disabled={triggering}
            className="label-mono text-[var(--stakes-muted)] hover:text-white"
            data-testid="rogue-trigger-btn"
            title="Force-fire a fresh directive (dev only)"
          >
            {triggering ? "[ DEPLOYING… ]" : "[ DEV · RE-FIRE ]"}
          </button>
        </div>
      )}
    </div>
  );
}
